/**
 * @fileoverview Main application router configuration
 * @module router/AppRouter
 */
import { ReactElement } from 'react'
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import { HomePage } from '@/features/home/pages/HomePage'
import { PerformanceByProductPage } from '@/features/home/pages/PerformanceByProductPage'
import { ProductStatisticsPage } from '@/features/home/pages/ProductStatisticsPage'
import { SignUpPage } from '@/features/auth/pages/SignUpPage'
import { AcceptInvitePage } from '@/features/auth/pages/AcceptInvitePage'
import { NotFoundPage } from '@/shared/pages/NotFoundPage'
import { authService } from '@/lib/authService'
import { ProductsPage, ProductAdminPage, CreateProductPage } from '@/features/products'
import { CreateProductFromListingPage } from '@/features/sku-alias'
import { BaselinkerImportPage } from '@/features/baselinker-import'
import { ShopifyImportPage } from '@/features/shopify-import'
import { CostOfSoldGoodsPage } from '@/features/cogs'
import { OrdersPage } from '@/features/orders/pages/OrdersPage'
import { AdSpendsPage } from '@/features/ad-spends/pages/AdSpendsPage'
import ImportTransactionsPage from '@/features/universal-transactions/pages/ImportTransactionsPage'
import { RequireOrg } from '@/features/orgs/components/RequireOrg'
import { LogoutPage } from '@/features/auth/pages/LogoutPage'
import { SettingsPage } from '@/features/settings'
import { StatusMappingsPage } from '@/features/status-mappings'
import { BaselinkerSetupPage } from '@/features/baselinker-setup'
import { IndirectCostsPage } from '@/features/indirect-costs'
import OnboardingPage from '@/features/onboarding/pages/OnboardingPage'
import { PaymentsPage, VatRulesPage } from '@/features/payments'
import { InvoicesPage } from '@/features/invoices'
import { LandingPage, PrivacyPolicyPage } from '@/features/landing'
import { LandingRedirect, RequireGuest } from '@/features/landing/components/LandingRedirect'
import { McpConnectPage } from '@/features/mcp/pages/McpConnectPage'
import { TagsPage } from '@/features/tags'

import { ShopifyAppLandingPage } from '@/features/shopify-app'
import { StoreVariantsDebugPage } from '@/features/debug/pages/StoreVariantsDebugPage'

/**
 * Guard component that requires authentication to access a route.
 * Redirects unauthenticated users to the landing page.
 *
 * @component
 * @example
 * ```tsx
 * <Route path="/dashboard" element={<RequireAuth><HomePage /></RequireAuth>} />
 * ```
 */
function RequireAuth({ children }: { children: ReactElement }): ReactElement {
  const isAuthed = authService.isAuthenticated()
  if (!isAuthed) {
    return <Navigate to="/" replace />
  }
  return children
}

type LegacySkusRedirectMode = 'list' | 'new' | 'needsReview' | 'detail'

function LegacySkusRedirect({ mode }: { mode: LegacySkusRedirectMode }): ReactElement {
  const location = useLocation()
  const params = useParams()

  let path = '/dashboard/management/products'
  if (mode === 'new') path = `${path}/new`
  if (mode === 'needsReview') path = `${path}/needs-review/create`
  if (mode === 'detail') {
    path = params.id ? `${path}/${params.id}` : path
  }

  return <Navigate to={`${path}${location.search}`} replace />
}

/**
 * Main application router component
 *
 * @component
 * @example
 * ```tsx
 * <AppRouter />
 * ```
 */
export function AppRouter(): ReactElement {
  return (
    <Routes>
      {/* Public landing pages with language routing — authenticated users go to dashboard */}
      <Route path="/" element={<LandingRedirect />} />

      <Route path="/en" element={<RequireGuest><LandingPage lang="en" /></RequireGuest>} />
      <Route path="/en/" element={<RequireGuest><LandingPage lang="en" /></RequireGuest>} />
      <Route path="/pl" element={<RequireGuest><LandingPage lang="pl" /></RequireGuest>} />
      <Route path="/pl/" element={<RequireGuest><LandingPage lang="pl" /></RequireGuest>} />

      {/* Privacy Policy pages */}
      <Route path="/en/privacy-policy" element={<PrivacyPolicyPage lang="en" />} />
      <Route path="/pl/privacy-policy" element={<PrivacyPolicyPage lang="pl" />} />

      {/* Shopify app landing - handles redirect from Shopify Admin */}
      <Route path="/shopify" element={<ShopifyAppLandingPage />} />

      {/* Protected dashboard routes */}
      <Route
        path="/dashboard"
        element={
          <RequireAuth>
            <RequireOrg>
              <HomePage />
            </RequireOrg>
          </RequireAuth>
        }
      />
      <Route
        path="/dashboard/performance"
        element={
          <RequireAuth>
            <RequireOrg>
              <PerformanceByProductPage />
            </RequireOrg>
          </RequireAuth>
        }
      />
      <Route
        path="/dashboard/product/:productId"
        element={
          <RequireAuth>
            <RequireOrg>
              <ProductStatisticsPage />
            </RequireOrg>
          </RequireAuth>
        }
      />
      <Route
        path="/dashboard/management/products"
        element={
          <RequireAuth>
            <RequireOrg>
              <ProductsPage />
            </RequireOrg>
          </RequireAuth>
        }
      />
      <Route
        path="/dashboard/management/products/new"
        element={
          <RequireAuth>
            <RequireOrg>
              <CreateProductPage />
            </RequireOrg>
          </RequireAuth>
        }
      />
      <Route
        path="/dashboard/management/products/needs-review/create"
        element={
          <RequireAuth>
            <RequireOrg>
              <CreateProductFromListingPage />
            </RequireOrg>
          </RequireAuth>
        }
      />
      <Route
        path="/dashboard/management/products/:id"
        element={
          <RequireAuth>
            <RequireOrg>
              <ProductAdminPage />
            </RequireOrg>
          </RequireAuth>
        }
      />
      <Route
        path="/dashboard/management/tags"
        element={
          <RequireAuth>
            <RequireOrg>
              <TagsPage />
            </RequireOrg>
          </RequireAuth>
        }
      />
      {/* Legacy routes - redirect to new Products paths */}
      <Route path="/dashboard/management/skus" element={<LegacySkusRedirect mode="list" />} />
      <Route path="/dashboard/management/skus/new" element={<LegacySkusRedirect mode="new" />} />
      <Route
        path="/dashboard/management/skus/needs-review/create"
        element={<LegacySkusRedirect mode="needsReview" />}
      />
      <Route path="/dashboard/management/skus/:id" element={<LegacySkusRedirect mode="detail" />} />
      <Route
        path="/dashboard/management/import/baselinker"
        element={
          <RequireAuth>
            <RequireOrg>
              <BaselinkerImportPage />
            </RequireOrg>
          </RequireAuth>
        }
      />
      <Route
        path="/dashboard/management/import/shopify"
        element={
          <RequireAuth>
            <RequireOrg>
              <ShopifyImportPage />
            </RequireOrg>
          </RequireAuth>
        }
      />
      <Route
        path="/dashboard/cogs"
        element={
          <RequireAuth>
            <RequireOrg>
              <CostOfSoldGoodsPage />
            </RequireOrg>
          </RequireAuth>
        }
      />
      <Route
        path="/dashboard/orders"
        element={
          <RequireAuth>
            <RequireOrg>
              <OrdersPage />
            </RequireOrg>
          </RequireAuth>
        }
      />
      <Route
        path="/dashboard/google-links"
        element={<Navigate to="/dashboard/management/products?view=needsReview&source=google-ads" replace />}
      />
      <Route
        path="/dashboard/ad-spends"
        element={
          <RequireAuth>
            <RequireOrg>
              <AdSpendsPage />
            </RequireOrg>
          </RequireAuth>
        }
      />
      <Route
        path="/dashboard/indirect-costs"
        element={
          <RequireAuth>
            <RequireOrg>
              <IndirectCostsPage />
            </RequireOrg>
          </RequireAuth>
        }
      />
      <Route
        path="/dashboard/payments"
        element={
          <RequireAuth>
            <RequireOrg>
              <PaymentsPage />
            </RequireOrg>
          </RequireAuth>
        }
      />
      <Route
        path="/dashboard/payments/vat-rules"
        element={
          <RequireAuth>
            <RequireOrg>
              <VatRulesPage />
            </RequireOrg>
          </RequireAuth>
        }
      />
      <Route
        path="/dashboard/invoices"
        element={
          <RequireAuth>
            <RequireOrg>
              <InvoicesPage />
            </RequireOrg>
          </RequireAuth>
        }
      />
      <Route
        path="/dashboard/transactions/universal/import"
        element={
          <RequireAuth>
            <RequireOrg>
              <ImportTransactionsPage />
            </RequireOrg>
          </RequireAuth>
        }
      />
      <Route
        path="/dashboard/settings"
        element={
          <RequireAuth>
            <RequireOrg>
              <SettingsPage />
            </RequireOrg>
          </RequireAuth>
        }
      />
      <Route
        path="/dashboard/settings/status-mappings"
        element={
          <RequireAuth>
            <RequireOrg>
              <StatusMappingsPage />
            </RequireOrg>
          </RequireAuth>
        }
      />
      <Route
        path="/dashboard/settings/integrations/baselinker/setup"
        element={
          <RequireAuth>
            <RequireOrg>
              <BaselinkerSetupPage />
            </RequireOrg>
          </RequireAuth>
        }
      />
      <Route
        path="/dashboard/settings/mcp"
        element={
          <RequireAuth>
            <RequireOrg>
              <McpConnectPage />
            </RequireOrg>
          </RequireAuth>
        }
      />

      {/* Debug pages (not linked in navigation) */}
      <Route
        path="/dashboard/debug/store-variants"
        element={
          <RequireAuth>
            <RequireOrg>
              <StoreVariantsDebugPage />
            </RequireOrg>
          </RequireAuth>
        }
      />

      {/* Legacy redirects for old routes */}
      <Route path="/management/*" element={<Navigate to="/dashboard/management/products" replace />} />
      <Route path="/cogs" element={<Navigate to="/dashboard/cogs" replace />} />
      <Route path="/orders" element={<Navigate to="/dashboard/orders" replace />} />
      <Route path="/google-links" element={<Navigate to="/dashboard/management/products?view=needsReview&source=google-ads" replace />} />
      <Route path="/ad-spends" element={<Navigate to="/dashboard/ad-spends" replace />} />
      <Route path="/indirect-costs" element={<Navigate to="/dashboard/indirect-costs" replace />} />
      <Route path="/payments/*" element={<Navigate to="/dashboard/payments" replace />} />
      <Route path="/transactions/*" element={<Navigate to="/dashboard/transactions/universal/import" replace />} />
      <Route path="/settings/*" element={<Navigate to="/dashboard/settings" replace />} />

      {/* Public auth routes */}
      <Route path="/signup" element={<SignUpPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/accept-invite/:orgId" element={<AcceptInvitePage />} />
      <Route
        path="/onboarding"
        element={
          <RequireAuth>
            <OnboardingPage />
          </RequireAuth>
        }
      />
      {/* Legacy route - redirect to new onboarding */}
      <Route
        path="/onboarding/create-org"
        element={<Navigate to="/onboarding" replace />}
      />
      <Route path="/logout" element={<LogoutPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
}
